import { buildGs1DigitalLinkUrl } from '@myplayer/gs1';
import type { DraftTargetLink, IdentifierRecord } from '@myplayer/schema';
import {
  IconCheck,
  IconClock,
  IconDownload,
  IconEdit,
  IconFilter,
  IconProducts,
  IconQrCode,
  IconTrash,
  IconUndo,
} from '@myplayer/ui';
import { formatAuditActionLabel, type AuditEntryRecord } from './audit-log-helpers.js';
import { CONTEXT_MENU_DIVIDER, type ContextMenuEntry } from './components/ui/context-menu.js';
import { copyMenuItem, externalUrlMenuItem } from './context-menu-settings-items.js';
import {
  buildQualifierDigitalLinkPreviewUrl,
  resolveQualifierDisplayUrl,
  resolveQualifierOption,
  resolveQualifierPrimaryPair,
  type QualifierOverrideRecord,
} from './product-qualifiers-helpers.js';
import { resolveRowDestinationLabel } from './products-helpers.js';
import { openQrCheckInNewTab } from './qr-check-helper.js';
import type { QrExportFormat } from './qr-generator.js';
import { resolveEffectiveHost } from './use-workspace-identifiers.js';

export {
  buildApiKeyContextMenuItems,
  buildCustomDomainContextMenuItems,
  buildDefaultDomainContextMenuItems,
  buildGoogleAnalyticsContextMenuItems,
  buildMemberContextMenuItems,
  buildWorkspaceContextMenuItems,
  copyMenuItem,
  externalUrlMenuItem,
} from './context-menu-settings-items.js';

export function buildProductContextMenuItems(params: {
  readonly record: IdentifierRecord;
  readonly effectiveResolverHost?: string | undefined;
  readonly canEdit?: boolean | undefined;
  readonly onSelectRecord: (identifierKey: string) => void;
  readonly onEditRecord?: ((identifierKey: string) => void) | undefined;
}): ContextMenuEntry[] {
  const { record, effectiveResolverHost, canEdit = false, onSelectRecord, onEditRecord } = params;
  const host = resolveEffectiveHost(effectiveResolverHost);
  const resolverUrl =
    host.length > 0
      ? buildGs1DigitalLinkUrl({ host, ai: record.ai, primaryKey: record.primaryKey })
      : '';
  const destinationUrl = resolveRowDestinationLabel(record);
  const hasValidDestUrl = /^https?:\/\//i.test(destinationUrl);

  return [
    {
      id: 'open-product',
      label: 'Open product details',
      icon: <IconProducts />,
      onSelect: () => onSelectRecord(record.identifierKey),
    },
    canEdit &&
      onEditRecord !== undefined && {
        id: 'edit-product',
        label: 'Edit product',
        icon: <IconEdit />,
        onSelect: () => onEditRecord(record.identifierKey),
      },
    {
      id: 'check-qr',
      label: 'Check printed QR',
      icon: <IconQrCode />,
      onSelect: () => openQrCheckInNewTab(record.identifierKey),
    },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-gtin',
      `Copy GTIN (${record.primaryKey})`,
      record.primaryKey,
      `Copied GTIN ${record.primaryKey}.`,
    ),
    resolverUrl.length > 0 &&
      copyMenuItem(
        'copy-resolver-url',
        'Copy GS1 Digital Link URL',
        resolverUrl,
        'Copied GS1 Digital Link URL.',
      ),
    hasValidDestUrl &&
      copyMenuItem(
        'copy-destination-url',
        'Copy destination URL',
        destinationUrl,
        'Copied destination URL.',
      ),
    hasValidDestUrl &&
      externalUrlMenuItem('open-destination-url', 'Open destination in new tab', destinationUrl),
  ];
}

export function buildTargetLinkContextMenuItems(params: {
  readonly link: DraftTargetLink;
  readonly index: number;
  readonly canEdit: boolean;
  readonly canRemoveLink: boolean;
  readonly savingDraft: boolean;
  readonly onStartEditLink: (idx: number) => void;
  readonly onSetMainLink?: ((idx: number) => Promise<unknown>) | undefined;
  readonly onScheduleLink?: ((url: string) => void) | undefined;
  readonly onMarkLinkRemoved: (idx: number) => Promise<unknown>;
  readonly onRestoreLink: (idx: number) => Promise<unknown>;
}): ContextMenuEntry[] {
  const {
    link,
    index,
    canEdit,
    canRemoveLink,
    savingDraft,
    onStartEditLink,
    onSetMainLink,
    onScheduleLink,
    onMarkLinkRemoved,
    onRestoreLink,
  } = params;
  const isRemoved = link.removed === true;
  const isMain = link.defaultLink && !isRemoved;
  const hasValidHref = !isRemoved && /^https?:\/\//i.test(link.href);

  return [
    canEdit &&
      !isRemoved && {
        id: 'edit-link',
        label: 'Edit link',
        icon: <IconEdit />,
        disabled: savingDraft,
        onSelect: () => onStartEditLink(index),
      },
    canEdit &&
      !isRemoved &&
      !isMain &&
      onSetMainLink !== undefined && {
        id: 'set-main-link',
        label: 'Set as main scan destination',
        icon: <IconCheck />,
        disabled: savingDraft,
        onSelect: () => void onSetMainLink(index),
      },
    canEdit &&
      !isRemoved &&
      !isMain &&
      onScheduleLink !== undefined && {
        id: 'schedule-link',
        label: 'Schedule this link',
        icon: <IconClock />,
        disabled: savingDraft,
        onSelect: () => onScheduleLink(link.href),
      },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-link-url',
      'Copy destination URL',
      link.href,
      'Copied link destination URL.',
    ),
    copyMenuItem(
      'copy-link-type',
      `Copy link type (${link.linkType})`,
      link.linkType,
      `Copied link type ${link.linkType}.`,
    ),
    hasValidHref && externalUrlMenuItem('open-link-url', 'Open destination in new tab', link.href),
    CONTEXT_MENU_DIVIDER,
    canEdit &&
      !isRemoved &&
      canRemoveLink && {
        id: 'remove-link',
        label: 'Remove link',
        icon: <IconTrash />,
        destructive: true,
        disabled: savingDraft,
        onSelect: () => void onMarkLinkRemoved(index),
      },
    canEdit &&
      isRemoved && {
        id: 'restore-link',
        label: 'Restore link',
        icon: <IconUndo />,
        disabled: savingDraft,
        onSelect: () => void onRestoreLink(index),
      },
  ];
}

export function buildQualifierContextMenuItems(params: {
  readonly record: QualifierOverrideRecord;
  readonly parentAi: IdentifierRecord['ai'];
  readonly parentPrimaryKey: string;
  readonly resolvedHost: string;
  readonly canEdit: boolean;
  readonly saving: boolean;
  readonly onEdit: (rec: QualifierOverrideRecord) => void;
  readonly onRemove: (rec: QualifierOverrideRecord) => Promise<void> | void;
}): ContextMenuEntry[] {
  const { record, parentAi, parentPrimaryKey, resolvedHost, canEdit, saving, onEdit, onRemove } =
    params;
  const pair = resolveQualifierPrimaryPair(record);
  const opt = resolveQualifierOption(pair.code);
  const displayUrl = resolveQualifierDisplayUrl(record);
  const previewUri = buildQualifierDigitalLinkPreviewUrl({
    host: resolvedHost,
    parentAi,
    parentPrimaryKey,
    qualifierCode: pair.code,
    qualifierValue: pair.value,
  });
  const hasValidTargetUrl = /^https?:\/\//i.test(displayUrl);

  return [
    canEdit && {
      id: 'edit-qualifier',
      label: 'Edit qualifier',
      icon: <IconEdit />,
      disabled: saving,
      onSelect: () => onEdit(record),
    },
    CONTEXT_MENU_DIVIDER,
    previewUri.length > 0 &&
      copyMenuItem(
        'copy-qualifier-uri',
        'Copy qualifier Digital Link URL',
        previewUri,
        'Copied qualifier Digital Link URL.',
      ),
    copyMenuItem(
      'copy-qualifier-value',
      `Copy ${opt.shortLabel} value (${pair.value})`,
      pair.value,
      `Copied ${opt.shortLabel} ${pair.value}.`,
    ),
    hasValidTargetUrl &&
      copyMenuItem(
        'copy-qualifier-target',
        'Copy target URL',
        displayUrl,
        'Copied qualifier target URL.',
      ),
    hasValidTargetUrl &&
      externalUrlMenuItem('open-qualifier-target', 'Open target URL in new tab', displayUrl),
    CONTEXT_MENU_DIVIDER,
    canEdit && {
      id: 'remove-qualifier',
      label: 'Remove qualifier',
      icon: <IconTrash />,
      destructive: true,
      disabled: saving,
      onSelect: () => void onRemove(record),
    },
  ];
}

export function buildQrCardContextMenuItems(params: {
  readonly resolvedQrUrl: string;
  readonly activeTab?: 'gs1' | 'styled' | undefined;
  readonly onSwitchTab?: ((tab: 'gs1' | 'styled') => void) | undefined;
  readonly onDownload?: ((format: QrExportFormat) => void) | undefined;
  readonly onOpenQrCheck?: (() => void) | undefined;
}): ContextMenuEntry[] {
  const { resolvedQrUrl, activeTab, onSwitchTab, onDownload, onOpenQrCheck } = params;
  return [
    resolvedQrUrl.length > 0 &&
      copyMenuItem('copy-qr-url', 'Copy resolver link', resolvedQrUrl, 'Copied resolver link.'),
    CONTEXT_MENU_DIVIDER,
    onDownload !== undefined && {
      id: 'download-qr-svg',
      label: 'Download SVG',
      icon: <IconDownload />,
      onSelect: () => onDownload('SVG'),
    },
    onDownload !== undefined && {
      id: 'download-qr-png',
      label: 'Download PNG',
      icon: <IconDownload />,
      onSelect: () => onDownload('PNG'),
    },
    onDownload !== undefined && {
      id: 'download-qr-pdf',
      label: 'Download Print PDF',
      icon: <IconDownload />,
      onSelect: () => onDownload('PDF'),
    },
    CONTEXT_MENU_DIVIDER,
    activeTab !== undefined &&
      onSwitchTab !== undefined && {
        id: 'toggle-qr-tab',
        label: activeTab === 'gs1' ? 'Switch to Styled version' : 'Switch to GS1 print version',
        icon: <IconQrCode />,
        onSelect: () => onSwitchTab(activeTab === 'gs1' ? 'styled' : 'gs1'),
      },
    onOpenQrCheck !== undefined && {
      id: 'check-printed-qr',
      label: 'Check printed QR',
      icon: <IconQrCode />,
      onSelect: onOpenQrCheck,
    },
  ];
}

export function buildOverviewProductContextMenuItems(params: {
  readonly identifierKey: string;
  readonly primaryKey: string;
  readonly title?: string | undefined;
  readonly actionLabel?: string | undefined;
  readonly onOpenProduct?: (() => void) | undefined;
}): ContextMenuEntry[] {
  const { identifierKey, primaryKey, title, actionLabel, onOpenProduct } = params;
  return [
    onOpenProduct !== undefined && {
      id: 'open-in-products',
      label: actionLabel ?? 'Open in Products',
      icon: <IconProducts />,
      onSelect: onOpenProduct,
    },
    {
      id: 'check-printed-qr',
      label: 'Check printed QR',
      icon: <IconQrCode />,
      onSelect: () => openQrCheckInNewTab(identifierKey),
    },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-gtin',
      `Copy GTIN (${primaryKey})`,
      primaryKey,
      `Copied GTIN ${primaryKey}.`,
    ),
    title !== undefined &&
      title.length > 0 &&
      copyMenuItem('copy-product-title', 'Copy product name', title, 'Copied product name.'),
  ];
}

export function buildAuditEntryContextMenuItems(params: {
  readonly entry: AuditEntryRecord;
  readonly onFilterBySearch?: ((query: string) => void) | undefined;
  readonly onFilterByAction?: ((action: string) => void) | undefined;
}): ContextMenuEntry[] {
  const { entry, onFilterBySearch, onFilterByAction } = params;
  const actionLabel = formatAuditActionLabel(entry.action);
  const snapshotPayload = JSON.stringify(
    {
      auditId: entry.auditId,
      action: entry.action,
      resourceType: entry.resourceType,
      resourceId: entry.resourceId,
      beforeSnapshot: entry.beforeSnapshot ?? null,
      afterSnapshot: entry.afterSnapshot ?? null,
    },
    null,
    2,
  );

  return [
    onFilterBySearch !== undefined && {
      id: 'filter-resource',
      label: `Filter by resource (${entry.resourceId})`,
      icon: <IconFilter />,
      onSelect: () => onFilterBySearch(entry.resourceId),
    },
    onFilterBySearch !== undefined && {
      id: 'filter-actor',
      label: `Filter by actor (${entry.actorUid})`,
      icon: <IconFilter />,
      onSelect: () => onFilterBySearch(entry.actorUid),
    },
    onFilterByAction !== undefined && {
      id: 'filter-action',
      label: `Filter by action (${actionLabel})`,
      icon: <IconFilter />,
      onSelect: () => onFilterByAction(entry.action),
    },
    CONTEXT_MENU_DIVIDER,
    copyMenuItem(
      'copy-resource-id',
      'Copy resource ID',
      entry.resourceId,
      `Copied resource ID ${entry.resourceId}.`,
    ),
    copyMenuItem(
      'copy-actor-uid',
      'Copy actor UID',
      entry.actorUid,
      `Copied actor UID ${entry.actorUid}.`,
    ),
    entry.entryHash !== undefined &&
      entry.entryHash.length > 0 &&
      copyMenuItem(
        'copy-entry-hash',
        'Copy SHA-256 entry hash',
        entry.entryHash,
        'Copied SHA-256 entry hash.',
      ),
    copyMenuItem(
      'copy-snapshot-json',
      'Copy before/after JSON',
      snapshotPayload,
      'Copied audit snapshot JSON.',
    ),
  ];
}
